import { TFunction } from 'i18next';
import { z } from 'zod';

export const teamMemberCapacitySchema = (t: TFunction, isCreateMode: boolean = false) =>
  z
    .object({
      isOnDemandAvailability: z.boolean(),
      capacityPerDay: z.number({
        invalid_type_error: t('common:error.invalidNumber'),
      }),
      billableCapacity: z.number(),
      corporateFunctionCapacity: z.number(),
    })
    .refine(
      data => {
        // Skip validation if on-demand availability is enabled
        if (data.isOnDemandAvailability) {
          return true;
        }
        // Validate capacityPerDay range when not on-demand
        return data.capacityPerDay >= 0 && data.capacityPerDay <= 24;
      },
      data => ({
        message:
          data.capacityPerDay > 24
            ? t('common:error.maxValue', { value: 24 })
            : t('common:error.minValueMoreThan', { value: 0 }),
        path: ['capacityPerDay'],
      })
    )
    .refine(
      data => {
        // Skip validation if on-demand availability is enabled
        if (data.isOnDemandAvailability) {
          return true;
        }
        // Capacity cannot be 0 when not on-demand
        return data.capacityPerDay > 0;
      },
      {
        message: isCreateMode
          ? t('authenticated.team:details.tabs.capacity.error.capacityZeroCreate')
          : t('authenticated.team:details.tabs.capacity.error.capacityZeroEdit'),
        path: ['capacityPerDay'],
      }
    )
    .refine(
      data => {
        // Skip validation if on-demand availability is enabled
        if (data.isOnDemandAvailability) {
          return true;
        }
        // Billable + Corporate Function should equal 100%
        return Math.abs(data.billableCapacity + data.corporateFunctionCapacity - 100) < 0.01;
      },
      {
        message: t('authenticated.team:details.tabs.capacity.error.capacitySum'),
        path: ['billableCapacity'],
      }
    );

export type TeamMemberCapacityFormData = z.infer<ReturnType<typeof teamMemberCapacitySchema>>;
